<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import PEChangelogComponent from '@/components/editor/popup/PEChangelogComponent';
import PEProfileProgressComponent from '@/components/editor/PEProfileProgressComponent';
import { useScreenSize } from '@/composables/useScreenSize';
import '@sapudex/web-components/dist/MessageStrip.js';

import '@sapudex/web-components/dist/Switch.js';

defineOptions({
  name: 'PESecondaryNavigationBar',
});

const ROUTES = {
  EDIT: (profileId) => `/editor/${profileId}/edit`,
  PREVIEW: (profileId) => `/editor/${profileId}/preview`,
  PROFILE: (profileId) => `/profile/${profileId}`,
};

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const { isScreenSizeMoreMD } = useScreenSize();

const toggleChecked = ref(true);
const changelogOpen = ref(false);

function switchToEditMode() {
  goToPath(ROUTES.EDIT(route.params.profileId));
}

function switchToPreviewMode() {
  goToPath(ROUTES.PREVIEW(route.params.profileId));
}

function openProfile() {
  const resolvedRoute = router.resolve({
    path: ROUTES.PROFILE(route.params.profileId),
  });
  window.open(resolvedRoute.href, '_blank');
}

function goToPath(path) {
  if (isNotCurrentPath(path)) {
    router.push({ path });
  }
}

function isNotCurrentPath(path) {
  return !(router.currentRoute.value.fullPath === path);
}

function onToggleChange(event) {
  if (event.target.checked) {
    switchToEditMode();
    setTimeout(() => {
      window.$eventBus.$emit('editorToggleButtonClicked');
    });
  } else {
    window.$eventBus.$emit('editorToggleButtonClicked');
    setTimeout(() => {
      switchToPreviewMode();
    });
  }
}

function toggleChangelog() {
  changelogOpen.value = !changelogOpen.value;
  window.$eventBus.$emit('PFCustomEvent', { eventName: 'ChangeHistory' });
}

function updateToggleState(path) {
  toggleChecked.value = path.endsWith('edit');
}

onMounted(() => updateToggleState(route.path));
watch(() => route.path, updateToggleState);

const publishDate = computed(() => store.state.partnerProfile?.profileData?.publishDate ?? null);
const changelogData = computed(() => store.state.changelog.changelogData ?? null);

const status = computed(() => store.state.partnerProfile?.profileData?.status ?? 'DRAFT');

const profileStatus = computed(() => {
  if (publishDate.value) {
    return status.value === 'PUBLISHED'
      ? t('editor.header.profile.status.published', { date: lastPublishDate.value })
      : t('editor.header.profile.status.draft', { date: lastChangeDate.value });
  }
  return t('editor.header.profile.status.not.published');
});

const lastPublishDate = computed(() => {
  if (publishDate.value) {
    const myDate = new Date(publishDate.value);
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return myDate.toLocaleString('en-US', options);
  }
  return '';
});
const lastChangeDate = computed(() => {
  if (!changelogData.value || changelogData.value.length === 0) {
    return '';
  }
  const lastChange = changelogData.value.find((entry) => entry.changeStatus === 'CHANGES');

  if (lastChange && lastChange.createdAt) {
    const myDate = new Date(lastChange.createdAt);
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return myDate.toLocaleString('en-US', options);
  }
  return '';
});
const isChanlogDataAvailable = computed(() => {
  return changelogData.value.length > 0;
});
const switchLabelText = computed(() => {
  return toggleChecked.value ? t('editor.header.mode.edit') : t('editor.header.mode.preview');
});
</script>

<template>
  <div class="col-span-full flex h-16 items-center md:h-18 md:justify-between">
    <PEProfileProgressComponent class="flex max-[1330px]:hidden" />
    <div v-show="isScreenSizeMoreMD" class="changelog-container flex items-center gap-2">
      <udex-message-strip design="Information" status-type="Icon" hide-close-button class="w-fit">
        {{ profileStatus }}
      </udex-message-strip>
      <udex-button
        v-show="isChanlogDataAvailable"
        class="open-change-log"
        @click="toggleChangelog()"
        size="Medium"
        design="Invisible"
      >
        {{ t('editor.header.show.changelog') }}
      </udex-button>
    </div>

    <div class="switch-container flex items-center justify-between gap-9 max-md:w-full">
      <div
        class="body-s-size flex w-[122px] items-center justify-between md:w-32 md:justify-end md:gap-2.5"
      >
        <span>{{ switchLabelText }}</span>
        <udex-switch
          id="PE-secondarynavigation-mode-toggle"
          :accessibleName="$t('editor.header.mode.accessible.name')"
          desktop
          :checked.prop="toggleChecked"
          @change="onToggleChange($event)"
        ></udex-switch>
      </div>
      <udex-button
        class="open-profile-button"
        v-show="publishDate"
        @click="openProfile"
        size="Medium"
        design="Invisible"
        >{{ t('editor.header.open.profile') }}</udex-button
      >
    </div>

    <PEChangelogComponent :toggleChangelog="toggleChangelog" :changelogOpen="changelogOpen" />
  </div>
</template>

<style lang="less" scoped>
udex-button {
  --udex-button-color-invisible-standard-default-text-and-icon: var(--color-blue-7);
}
</style>
